import React, { useState, useEffect, FC } from "react";
import { Vehicle } from "../models";
import vehiclesService from "services/vehiclesService";
import leaseCompaniesService, { LeaseCompany } from "services/leaseCompaniesService";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { Hub, KeyValue } from "../../common/models";
import orgDataService from "services/orgDataService";
import * as Yup from "yup";
import {} from "@mui/material/colors";
import MDButton from "components/MDButton";

export interface VehicleErrors {
  description: string;
  hubId: string;
  typeId: string;
  unitNumber: string;
  plateNumber: string;
  vin: string;
  odometer: string;
  statusId: string;
  capacity: string;
  make: string;
  year: string;
  color: string;
  cellNumber: string;
  modemSimNumber: string;
  modemEsnNumber: string;
  cameraDeviceNumber: string;
  deviceSerialNumber: string;
  leaseCompanyId: string;
}

const VehicleCreate = () => {
  const [vehicle, setVehicle] = useState<Vehicle>({ isActive: true, isSuspended: false } as Vehicle);
  const { id } = useParams();
  const navigate = useNavigate();
  const [hubs, setHubs] = useState<Hub[]>();
  const [types, setTypes] = useState<KeyValue[]>();
  const [statuses, setStatuses] = useState<KeyValue[]>();
  const [outOfServiceReasons, setOutOfServiceReasons] = useState<KeyValue[]>();
  const [leaseCompanies, setLeaseCompanies] = useState<LeaseCompany[]>([]);
  const [displayOutOfServiceReason, setDisplayOutOfServiceReason] = useState<boolean>(false);
  const [errors, setErrors] = useState<VehicleErrors>();

  useEffect(() => {
    orgDataService.getAllHubs().then((data) => setHubs(data));
    orgDataService.getVehicleTypes().then((data) => setTypes(data));
    orgDataService.getOutOfServiceReasons().then((data) => setOutOfServiceReasons(data));
    orgDataService.getVehicleStatuses().then((data) => {
      setStatuses(data);
    });
    leaseCompaniesService.getLeaseCompanies(false).then((data) => setLeaseCompanies(data));
  }, []);

  const vehicleSchema: any = Yup.object().shape({
    id: Yup.number().optional(),
    hubId: Yup.number().required("Hub is required"),
    typeId: Yup.number().required("Type is required"),
    description: Yup.string().max(50, "Description must be 50 characters or less"),
    plateNumber: Yup.string().max(10, "Plate Number must be 10 charaters or less"),
    unitNumber: Yup.number().required("Unit Number is required").max(9223372036854775807).min(-9223372036854775808),
    vin: Yup.string().required().max(25,"VIN must be 25 characters or less"),
    odometer: Yup.number().required().max(999999),
    isActive: Yup.boolean().required().default(true),
    isSuspended: Yup.boolean().required().default(false),
    statusId: Yup.string().required("Status is required"),
    capacity: Yup.string().required().max(50, "Capacity must be 50 characters or less"),
    make: Yup.string().required().max(50, "Make must be 50 charactes or less"),
    year: Yup.number().required().test('len', "Year must be 10 characters or less", val => val.toString().length <=4),
    color: Yup.string().required().max(10,"Color must be 10 characters or less"),
    comments: Yup.string(),
    cellNumber: Yup.string().max(10,"Cell Number must be 10 characters or less"),
    modemSimNumber: Yup.string().max(50,"Modem SIM Number must be 50 characters or less"),
    modemEsnNumber: Yup.string().max(50, "modem ESN Number must be 50 characters or less"),
    cameraDeviceNumber: Yup.string().max(50, "Camera Device Number must be 50 characters or less"),
    deviceSerialNumber: Yup.string().max(50, "Device Serial Number must be 50 characters or less"),
    leaseCompanyId: Yup.number().nullable(),
  });

  function validate() {
    try {
      vehicleSchema.validateSync(vehicle, { abortEarly: false });
      setErrors({
        description: "",
        hubId: "",
        typeId: "",
        unitNumber: "",
        plateNumber: "",
        vin: "",
        odometer: "",
        statusId: "",
        capacity: "",
        make: "",
        year: "",
        color: "",
        cellNumber:"",
        modemSimNumber: "",
        modemEsnNumber: "",
        cameraDeviceNumber: "",
        deviceSerialNumber: "",
        leaseCompanyId: ""
      });
      return true;
    } catch (error) {
      let validation = error as Yup.ValidationError;
      const newErrors = validation.inner.reduce((acc: any, cur: any) => {
        acc[cur.path] = cur.message;
        return acc;
      }, {});
      setErrors(newErrors);
      return false;
    }
  }
  const save = () => {
    if (!vehicle || !validate()) return;

    vehiclesService.saveVehicle(vehicle).then((r) => {
      navigate("/vehicles");
    });
  };

  return (
    <>
      {vehicle && (
        <>
          <Box component="fieldset" sx={{ m: 2, p: 2 }}>
            <legend>Vehicle Info</legend>
            <Grid container spacing="2">
              <Grid item xs={12} md={4}>
                <TextField
                  label="Description"
                  value={vehicle.description}
                  onChange={(e) => setVehicle({ ...vehicle, description: e.target.value })}
                  fullWidth
                  margin="normal"
                  inputProps={{maxLength:50}}
                  error={!!errors?.description}
                  helperText={errors?.description}
                />

                <TextField
                  label="Unit Number"
                  type="number"
                  value={vehicle.unitNumber}
                  onChange={(e) => setVehicle({ ...vehicle, unitNumber: +e.target.value })}
                  fullWidth
                  margin="normal"
                  required
                  error={!!errors?.unitNumber}
                  helperText={errors?.unitNumber}
                />
                <TextField
                  label="Year"
                  type="number"
                  value={vehicle.year}
                  onChange={(e) => setVehicle({ ...vehicle, year: e.target.value })}
                  fullWidth
                  margin="normal"
                  required
                  inputProps={{max:3000}}
                  error={!!errors?.year}
                  helperText={errors?.year}
                />

                <TextField
                  label="Make"
                  value={vehicle.make}
                  onChange={(e) => setVehicle({ ...vehicle, make: e.target.value })}
                  fullWidth
                  margin="normal"
                  required
                  inputProps={{maxLength:50}}
                  error={!!errors?.make}
                  helperText={errors?.make}
                />

                <TextField
                  label="Color"
                  value={vehicle.color}
                  onChange={(e) => setVehicle({ ...vehicle, color: e.target.value })}
                  fullWidth
                  margin="normal"
                  required
                  inputProps={{maxLength:10}}
                  error={!!errors?.color}
                  helperText={errors?.color}
                />

                <TextField
                  label="Plate Number"
                  value={vehicle.plateNumber}
                  onChange={(e) => setVehicle({ ...vehicle, plateNumber: e.target.value })}
                  fullWidth
                  margin="normal"
                  inputProps={{maxLength:10}}
                  error={!!errors?.plateNumber}
                  helperText={errors?.plateNumber}
                />

                <TextField
                  label="VIN"
                  value={vehicle.vin}
                  onChange={(e) => setVehicle({ ...vehicle, vin: e.target.value })}
                  fullWidth
                  margin="normal"
                  required
                  inputProps={{maxLength:25}}
                  error={!!errors?.vin}
                  helperText={errors?.vin}
                />
              </Grid>
              <Grid item xs={12} md={4} sx={{ ml: 4 }}>
                <FormControl sx={{ mt: 2 }} fullWidth>
                  <InputLabel className={!!errors?.hubId ? "Mui-error" : ""} id="hubLabelId">
                    Hub *
                  </InputLabel>
                  <Select
                    id="hubId"
                    name="hubId"
                    labelId="hubLabelId"
                    label="Hub *"
                    sx={{ minWidth: 210 }}
                    required
                    value={vehicle.hubId}
                    error={!!errors?.hubId}
                    onChange={(e) =>
                      setVehicle({
                        ...vehicle,
                        hubId: +e.target.value,
                      })
                    }
                    displayEmpty
                  >
                    {hubs &&
                      hubs.map((h, index) => {
                        return (
                          <MenuItem key={index} value={h.id}>
                            {h.name}
                          </MenuItem>
                        );
                      })}
                  </Select>
                  <FormHelperText className="Mui-error">{errors?.hubId}</FormHelperText>
                </FormControl>
                <FormControl sx={{ mt: 2 }} fullWidth>
                  <InputLabel className={!!errors?.typeId ? "Mui-error" : ""} id="typeId">
                    Type *
                  </InputLabel>
                  <Select
                    id="typeId"
                    name="typeId"
                    labelId="typeId"
                    label="type"
                    sx={{ minWidth: 210 }}
                    value={vehicle.typeId}
                    required
                    error={!!errors?.typeId}
                    onChange={(e) =>
                      setVehicle({
                        ...vehicle,
                        typeId: +e.target.value,
                      })
                    }
                    displayEmpty
                  >
                    {types &&
                      types.map((t, index) => {
                        return (
                          <MenuItem key={index} value={t.id}>
                            {t.name}
                          </MenuItem>
                        );
                      })}
                  </Select>
                  <FormHelperText className="Mui-error">{errors?.typeId}</FormHelperText>
                </FormControl>

                <FormControl sx={{ mt: 2 }} fullWidth>
                  <InputLabel className={!!errors?.statusId ? "Mui-error" : ""} id="statusId">
                    Status *
                  </InputLabel>
                  <Select
                    id="statusId"
                    name="statusId"
                    labelId="statusId"
                    label="Status"
                    sx={{ minWidth: 210 }}
                    required
                    error={!!errors?.statusId}
                    value={vehicle.statusId}
                    onChange={(e) => {
                      setVehicle({ ...vehicle, statusId: e.target.value });
                      let outOfStatusId = statuses?.find((s) => s.name === "Out Of Service");
                      setDisplayOutOfServiceReason(+e.target.value === outOfStatusId?.id);
                    }}
                    displayEmpty
                  >
                    {statuses &&
                      statuses.map((s, index) => {
                        return (
                          <MenuItem key={index} value={s.id}>
                            {s.name}
                          </MenuItem>
                        );
                      })}
                  </Select>
                  <FormHelperText className="Mui-error">{errors?.statusId}</FormHelperText>
                </FormControl>
                {displayOutOfServiceReason && (
                  <FormControl sx={{ mt: 2, minWidth: 120 }} fullWidth>
                    <InputLabel id="outofServiceReasonId">Out of Service Reason</InputLabel>
                    <Select
                      id="outofServiceReasonId"
                      name="outofServiceReasonId"
                      labelId="outofServiceReasonId"
                      label="Out of Service Reason"
                      sx={{ minWidth: 210 }}
                      value={vehicle.outOfServiceReasonId}
                      onChange={(e) =>
                        setVehicle({
                          ...vehicle,
                          outOfServiceReasonId: +e.target.value,
                        })
                      }
                      displayEmpty
                    >
                      {outOfServiceReasons &&
                        outOfServiceReasons.map((s, index) => {
                          return (
                            <MenuItem key={index} value={s.id}>
                              {s.name}
                            </MenuItem>
                          );
                        })}
                    </Select>
                  </FormControl>
                )}
                <TextField
                  label="Odometer"
                  type="number"
                  value={vehicle.odometer}
                  onChange={(e) => setVehicle({ ...vehicle, odometer: +e.target.value })}
                  fullWidth
                  margin="normal"
                  required
                  inputProps={{max:999999}}
                  error={!!errors?.odometer}
                  helperText={errors?.odometer}
                />

                <TextField
                  label="Capacity"
                  value={vehicle.capacity}
                  onChange={(e) => setVehicle({ ...vehicle, capacity: e.target.value })}
                  fullWidth
                  margin="normal"
                  required
                  inputProps={{maxLength:50}}
                  error={!!errors?.capacity}
                  helperText={errors?.capacity}
                />

                <TextField
                  label="Cell Number"
                  value={vehicle.cellNumber}
                  onChange={(e) => setVehicle({ ...vehicle, cellNumber: e.target.value })}
                  fullWidth
                  margin="normal"
                  inputProps={{maxLength:10}}
                  error={!!errors?.cellNumber}
                  helperText={errors?.cellNumber}
                />

                <FormControl sx={{ mt: 2 }} fullWidth>
                  <InputLabel shrink id="leaseCompanyLabelId">Lease Company</InputLabel>
                  <Select
                    id="leaseCompanyId"
                    name="leaseCompanyId"
                    labelId="leaseCompanyLabelId"
                    label="Lease Company"
                    notched
                    sx={{ minWidth: 210 }}
                    value={vehicle.leaseCompanyId ?? ""}
                    error={!!errors?.leaseCompanyId}
                    onChange={(e) =>
                      setVehicle({
                        ...vehicle,
                        leaseCompanyId: e.target.value === "" ? null : +e.target.value,
                      })
                    }
                    displayEmpty
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {leaseCompanies.map((lc) => (
                      <MenuItem key={lc.id} value={lc.id}>
                        {lc.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText className="Mui-error">{errors?.leaseCompanyId}</FormHelperText>
                </FormControl>

                <TextField
                  label="Comments"
                  value={vehicle.comments}
                  onChange={(e) => setVehicle({ ...vehicle, comments: e.target.value })}
                  fullWidth
                  multiline
                  rows={3}
                  margin="normal"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      defaultChecked
                      checked={vehicle.isActive}
                      onChange={(e) => setVehicle({ ...vehicle, isActive: e.target.checked })}
                    />
                  }
                  label="Is Active"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={vehicle.isSuspended}
                      onChange={(e) => setVehicle({ ...vehicle, isSuspended: e.target.checked })}
                    />
                  }
                  label="Is Suspended"
                />
              </Grid>
            </Grid>
          </Box>

          <Box component="fieldset" sx={{ m: 2, p: 2 }}>
            <legend>Device/Modem Details</legend>
            <Grid container spacing="2">
              <Grid item xs={12} md={4}>
                <TextField
                  label="Modem SIM Number"
                  value={vehicle.modemSimNumber}
                  onChange={(e) => setVehicle({ ...vehicle, modemSimNumber: e.target.value })}
                  fullWidth
                  margin="normal"
                  inputProps={{maxLength:50}}
                  error={!!errors?.modemSimNumber}
                  helperText={errors?.modemSimNumber}
                />
                <TextField
                  label="Modem ESN Number"
                  value={vehicle.modemEsnNumber}
                  onChange={(e) => setVehicle({ ...vehicle, modemEsnNumber: e.target.value })}
                  fullWidth
                  margin="normal"
                  inputProps={{maxLength:50}}
                  error={!!errors?.modemEsnNumber}
                  helperText={errors?.modemEsnNumber}
                />
                <TextField
                  label="Camera Device Number"
                  value={vehicle.cameraDeviceNumber}
                  onChange={(e) => setVehicle({ ...vehicle, cameraDeviceNumber: e.target.value })}
                  fullWidth
                  margin="normal"
                  inputProps={{maxLength:50}}
                  error={!!errors?.cameraDeviceNumber}
                  helperText={errors?.cameraDeviceNumber}
                />
                <TextField
                  label="Device Serial Number"
                  value={vehicle.deviceSerialNumber}
                  onChange={(e) => setVehicle({ ...vehicle, deviceSerialNumber: e.target.value })}
                  fullWidth
                  margin="normal"
                  inputProps={{maxLength:50}}
                  error={!!errors?.deviceSerialNumber}
                  helperText={errors?.deviceSerialNumber}
                />
              </Grid>
            </Grid>
          </Box>
          <Grid item xs={12} justifyItems="space-between">
            <Grid container spacing={2} sx={{ pt: 2, pb: 2, pr: 4 }}>
              <Grid item xs={12} style={{ display: "flex", justifyContent: "flex-end" }}>
                <MDButton variant="text" color="secondary" to="/vehicles" component={RouterLink}>
                  Cancel
                </MDButton>
                <MDButton variant="contained" color="dark" onClick={save}>
                  Save
                </MDButton>
              </Grid>
            </Grid>
          </Grid>
        </>
      )}
   </>
  );
};

export default VehicleCreate;
